// Client for the server's HRD bridge (handlers/hrd.ts) — GET-only,
// unauthenticated, same-origin, so no `credentials: 'include'` (unlike the
// session-cookie Kratos calls in kratos.utils).

export type FetchHrdConfigResult =
  | { kind: 'ok'; brandProviders: Record<string, string | null> }
  | { kind: 'error' }

export const fetchHrdConfig = async (signal?: AbortSignal): Promise<FetchHrdConfigResult> => {
  try {
    const response = await fetch('/hrd/config', { headers: { accept: 'application/json' }, signal })
    if (!response.ok) {
      return { kind: 'error' }
    }
    const body = (await response.json()) as { brandProviders?: Record<string, string | null> }
    return { kind: 'ok', brandProviders: body.brandProviders ?? {} }
  } catch {
    return { kind: 'error' }
  }
}

// The domain must be stripped here, in the browser, before anything goes
// over the wire — sending the full email would put it verbatim in
// access-log `http.url` and the error handler's `req.url`.
export const extractEmailDomain = (email: string): string | null => {
  const at = email.lastIndexOf('@')
  if (at < 0) {
    return null
  }
  const domain = email
    .slice(at + 1)
    .trim()
    .toLowerCase()
  return domain || null
}

export type FetchHrdLookupResult =
  | { kind: 'found'; provider: string }
  | { kind: 'not_found' }
  | { kind: 'rate_limited' }
  | { kind: 'error' }

export const fetchHrdLookup = async (
  email: string,
  signal?: AbortSignal,
): Promise<FetchHrdLookupResult> => {
  const domain = extractEmailDomain(email)
  if (!domain) {
    // No network round trip for something the server would 404 anyway —
    // and never a chance to leak the unparsable input in a URL.
    return { kind: 'not_found' }
  }
  try {
    const response = await fetch(`/hrd/lookup?domain=${encodeURIComponent(domain)}`, {
      headers: { accept: 'application/json' },
      signal,
    })
    if (response.status === 404) {
      return { kind: 'not_found' }
    }
    // Distinct from a generic failure — the caller shows a "wait and retry"
    // message instead of the "something went wrong" one (see useSsoRoute).
    if (response.status === 429) {
      return { kind: 'rate_limited' }
    }
    if (!response.ok) {
      return { kind: 'error' }
    }
    const body = (await response.json()) as { provider?: string }
    return typeof body.provider === 'string' && body.provider
      ? { kind: 'found', provider: body.provider }
      : { kind: 'error' }
  } catch {
    return { kind: 'error' }
  }
}
